// File store for managing file state
import { create } from 'zustand';
import { AudioFile, Folder, FileType, UploadProgress, FileStatus } from '@types';
import { supabaseAWSService } from '@services/supabase-aws.service';
import { useAuthStore } from './useAuthStore';
import { uploadQueue } from '@utils/uploadQueue';
import { captureException } from '@utils/sentry';
import { uploadWizardProgressService } from '@services/upload-wizard-progress.service';
import { storageLimitService } from '@services/storage-limit.service';
import { throwIfManuallySuspended } from '@utils/suspension';

const activeFileService = supabaseAWSService;

const FILES_CACHE_KEY = 'fyle-files-cache';
const FOLDERS_CACHE_KEY = 'fyle-folders-cache';
const FILES_CACHE_TTL = 10 * 60 * 1000; // 10 min – fresh cache, no network needed
const FILES_CACHE_STALE_DISPLAY = 30 * 60 * 1000; // 30 min – show stale cache immediately, reload in background
const FOLDERS_CACHE_STALE_DISPLAY = 30 * 60 * 1000;
const FILES_CACHE_MAX = 300;
const USER_STATE_STORAGE_KEY = 'fyle-user-state';

/** Synchronously hydrate files and folders from sessionStorage */
function getHydratedFileState(): Partial<Pick<FileStore, 'files' | 'folders' | 'lastFetchedAt' | 'filesCacheUserId'>> {
  if (typeof window === 'undefined') return {};
  const out: Partial<Pick<FileStore, 'files' | 'folders' | 'lastFetchedAt' | 'filesCacheUserId'>> = {};
  try {
    const userRaw = sessionStorage.getItem(USER_STATE_STORAGE_KEY);
    if (!userRaw) return {};
    const userId = (JSON.parse(userRaw) as { id?: string })?.id;
    if (!userId) return {};
    
    const cacheRaw = sessionStorage.getItem(FILES_CACHE_KEY);
    if (cacheRaw) {
      const parsed = JSON.parse(cacheRaw) as { userId: string; ts: number; files: AudioFile[] };
      if (parsed.userId === userId && parsed.files?.length && (Date.now() - parsed.ts) <= FILES_CACHE_STALE_DISPLAY) {
        out.files = parsed.files;
        out.lastFetchedAt = parsed.ts;
        out.filesCacheUserId = userId;
      }
    }
    const foldersRaw = sessionStorage.getItem(FOLDERS_CACHE_KEY);
    if (foldersRaw) {
      const parsed = JSON.parse(foldersRaw) as { userId: string; ts: number; folders: Folder[] };
      if (parsed.userId === userId && Array.isArray(parsed.folders) && (Date.now() - parsed.ts) <= FOLDERS_CACHE_STALE_DISPLAY) {
        out.folders = parsed.folders;
      }
    }
    return out;
  } catch {
    return {};
  }
}

/** Persist current files list to sessionStorage */
function persistFilesCache(files: AudioFile[], userId: string | null | undefined): void {
  if (typeof window === 'undefined' || !userId || files.length === 0) return;
  try {
    const ts = Date.now();
    const toCache = files.slice(0, FILES_CACHE_MAX);
    sessionStorage.setItem(FILES_CACHE_KEY, JSON.stringify({ userId, ts, files: toCache }));
  } catch (_) {}
}

/** Persist current folders list to sessionStorage */
function persistFoldersCache(folders: Folder[], userId: string | null | undefined): void {
  if (typeof window === 'undefined' || !userId) return;
  try {
    const ts = Date.now();
    sessionStorage.setItem(FOLDERS_CACHE_KEY, JSON.stringify({ userId, ts, folders }));
  } catch (_) {}
}

const PERSIST_CACHE_DEBOUNCE_MS = 400;
let persistCacheTimer: ReturnType<typeof setTimeout> | null = null;
let persistFoldersCacheTimer: ReturnType<typeof setTimeout> | null = null;

/** Debounced files cache write */
function schedulePersistFilesCache(files: AudioFile[], userId: string | null | undefined): void {
  if (typeof window === 'undefined') return;
  if (persistCacheTimer) clearTimeout(persistCacheTimer);
  persistCacheTimer = setTimeout(() => {
    persistCacheTimer = null;
    persistFilesCache(files, userId);
  }, PERSIST_CACHE_DEBOUNCE_MS);
}

/** Debounced folders cache write */
function schedulePersistFoldersCache(folders: Folder[], userId: string | null | undefined): void {
  if (typeof window === 'undefined') return;
  if (persistFoldersCacheTimer) clearTimeout(persistFoldersCacheTimer);
  persistFoldersCacheTimer = setTimeout(() => {
    persistFoldersCacheTimer = null;
    persistFoldersCache(folders, userId);
  }, PERSIST_CACHE_DEBOUNCE_MS);
}

/** Prefer local isStarred when it diverges from server */
function mergeFetchedFilesWithLocalStarred(prev: AudioFile[], fetched: AudioFile[]): AudioFile[] {
  return fetched.map((f) => {
    const local = prev.find((p) => p.id === f.id);
    return local && local.isStarred !== f.isStarred ? { ...f, isStarred: local.isStarred } : f;
  });
}

interface FileStore {
  files: AudioFile[];
  folders: Folder[];
  selectedFiles: string[];
  selectedFolders: string[];
  uploadProgress: Map<string, UploadProgress>;
  isLoading: boolean;
  error: string | null;
  searchQuery: string;
  lastFetchedAt: number | null;
  filesCacheUserId: string | null;

  // Actions
  fetchFiles: (forceRefresh?: boolean) => Promise<void>;
  fetchFolders: () => Promise<void>;
  createFolder: (name: string, parentId?: string) => Promise<Folder>;
  deleteFolder: (id: string) => Promise<void>;
  updateFolder: (id: string, updates: Partial<Folder>) => Promise<void>;
  moveFolder: (folderId: string, parentId: string | null) => Promise<void>;
  uploadFile: (file: File, metadata: Partial<AudioFile>) => Promise<void>;
  deleteFile: (id: string) => Promise<void>;
  updateFile: (id: string, updates: Partial<AudioFile>) => Promise<void>;
  toggleFileStar: (id: string) => Promise<void>;
  moveFile: (fileId: string, folderId: string | null) => Promise<void>;
  selectFile: (id: string) => void;
  deselectFile: (id: string) => void;
  clearSelection: () => void;
  toggleFileSelection: (id: string) => void;
  selectMultiple: (ids: string[]) => void;
  selectFolder: (id: string) => void;
  deselectFolder: (id: string) => void;
  clearFolderSelection: () => void;
  toggleFolderSelection: (id: string) => void;
  selectMultipleFolders: (ids: string[]) => void;
  clearAllSelections: () => void;
  getFilesByType: (type: FileType) => AudioFile[];
  searchFiles: (query: string) => Promise<void>;
  setError: (error: string | null) => void;
  getBlobUrl: (id: string) => Promise<string | null>;
  preloadUrlsForFiles: (files: AudioFile[]) => void;
  getUploadQueueStatus: () => { queueLength: number; activeCount: number; maxConcurrent: number };
  addFileLocally: (file: AudioFile) => void;
  patchFileLocally: (id: string, updates: Partial<AudioFile>) => void;
}

export const useFileStore = create<FileStore>((set, get) => {
  // Initialize upload queue
  uploadQueue.setUploadFunction(async (file: File, metadata: Partial<AudioFile>, onProgress?: (progress: UploadProgress) => void) => {
    const extendedMetadata = metadata as Partial<AudioFile> & {
      id?: string;
      type?: 'beat' | 'sample' | 'loop';
      folderId?: string;
      tags?: any[];
      bpm?: number;
      key?: string;
    };
    return supabaseAWSService.uploadFile(file, extendedMetadata, onProgress);
  });

  return {
    files: [],
    folders: [],
    selectedFiles: [],
    selectedFolders: [],
    uploadProgress: new Map(),
    isLoading: false,
    error: null,
    searchQuery: '',
    lastFetchedAt: null,
    filesCacheUserId: null,
    ...getHydratedFileState(),

    fetchFiles: async (forceRefresh: boolean = false) => {
      const state = get();
      const userId = useAuthStore.getState().user?.id;
      if (state.isLoading && !forceRefresh) return;

      const now = Date.now();
      const CACHE_DURATION = 5 * 60 * 1000;

      let restoredFromSessionStorage = false;
      if (typeof window !== 'undefined' && userId && !forceRefresh) {
        try {
          const raw = sessionStorage.getItem(FILES_CACHE_KEY);
          if (raw) {
            const parsed = JSON.parse(raw) as { userId: string; ts: number; files: AudioFile[] };
            const cacheAge = parsed.ts ? now - parsed.ts : Infinity;
            const isStaleButShow = cacheAge < FILES_CACHE_STALE_DISPLAY && cacheAge >= FILES_CACHE_TTL;
            if (parsed.userId === userId && parsed.files?.length && (cacheAge < FILES_CACHE_TTL || isStaleButShow)) {
              set({ files: parsed.files, isLoading: false, lastFetchedAt: parsed.ts, error: null, filesCacheUserId: userId });
              restoredFromSessionStorage = true;
              
              const forPreload = parsed.files.filter(f => f.s3_key && !f.cdn_url && f.userId).slice(0, 60);
              if (forPreload.length > 0) {
                activeFileService.preloadSignedUrls(forPreload, 60).catch(() => {});
              }
            }
          }
        } catch (_) {}
      }

      const mem = get();
      const hasCachedData = mem.files.length > 0;
      const memCacheAge = mem.lastFetchedAt ? now - mem.lastFetchedAt : Infinity;
      const cacheMatchesUser = !!userId && mem.filesCacheUserId === userId;
      const shouldUseCache =
        !forceRefresh &&
        hasCachedData &&
        memCacheAge < CACHE_DURATION &&
        cacheMatchesUser;

      if (shouldUseCache && !restoredFromSessionStorage) {
        return;
      }

      const isBackgroundRefresh = hasCachedData && !forceRefresh;
      set({
        ...(isBackgroundRefresh ? {} : { isLoading: true }),
        error: null,
        searchQuery: '',
        ...(hasCachedData && !forceRefresh ? {} : { files: [], filesCacheUserId: null })
      });
      
      const MAX_RETRIES = 3;
      let lastError: Error | null = null;
      
      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
          const fetchedFiles = await activeFileService.getFiles();
          const cacheOwnerId = useAuthStore.getState().user?.id;
          const prevFiles = get().files;
          const files = mergeFetchedFilesWithLocalStarred(prevFiles, fetchedFiles);

          set({
            files,
            isLoading: false,
            searchQuery: '',
            lastFetchedAt: now,
            error: null,
            filesCacheUserId: cacheOwnerId ?? null,
          });

          persistFilesCache(files, cacheOwnerId);

          const filesNeedingUrls = files
            .filter(file => file.s3_key && !file.cdn_url && file.userId)
            .slice(0, 60);

          if (filesNeedingUrls.length > 0) {
            activeFileService.preloadSignedUrls(filesNeedingUrls, 60).catch(() => {});
          }
          
          return;
        } catch (error) {
          lastError = error instanceof Error ? error : new Error(String(error));
          console.error(`❌ Fetch attempt ${attempt} failed:`, lastError);
          
          if (attempt < MAX_RETRIES) {
            const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
      }
      
      const errorMessage = lastError?.message || 'Failed to fetch files';
      console.error('❌ All fetch attempts failed:', errorMessage);
      
      set({ 
        error: errorMessage, 
        isLoading: false, 
        searchQuery: '',
        ...(hasCachedData ? {} : { files: [] })
      });
    },

    fetchFolders: async () => {
      const userId = useAuthStore.getState().user?.id;
      const state = get();
      let restoredFromCache = false;
      if (typeof window !== 'undefined' && userId) {
        try {
          const raw = sessionStorage.getItem(FOLDERS_CACHE_KEY);
          if (raw) {
            const parsed = JSON.parse(raw) as { userId: string; ts: number; folders: Folder[] };
            if (parsed.userId === userId && Array.isArray(parsed.folders) && (Date.now() - parsed.ts) <= FOLDERS_CACHE_STALE_DISPLAY) {
              set({ folders: parsed.folders });
              restoredFromCache = true;
            }
          }
        } catch (_) {}
      }
      try {
        const folders = await activeFileService.getFolders();
        set({ folders });
        if (typeof window !== 'undefined' && userId) {
          try {
            sessionStorage.setItem(FOLDERS_CACHE_KEY, JSON.stringify({ userId, ts: Date.now(), folders }));
          } catch (_) {}
        }
      } catch (error) {
        set({ error: 'Failed to fetch folders' });
        if (restoredFromCache && state.folders.length) set({ folders: state.folders });
      }
    },

    createFolder: async (name: string, parentId?: string) => {
      await throwIfManuallySuspended();
      const userId = useAuthStore.getState().user?.id;
      
      try {
        const folder = await activeFileService.createFolder(name, parentId);
        set((state) => {
          const newFolders = [...state.folders, folder];
          schedulePersistFoldersCache(newFolders, userId);
          return { folders: newFolders };
        });
        return folder;
      } catch (error) {
        set({ error: 'Failed to create folder' });
        throw error;
      }
    },

    deleteFolder: async (id: string) => {
      await throwIfManuallySuspended();
      const userId = useAuthStore.getState().user?.id;
      
      try {
        const folderFiles = get().files.filter(f => f.folderId === id);
        
        const getAllSubfolderIds = (parentId: string): string[] => {
          const directChildren = get().folders.filter(f => f.parentId === parentId).map(f => f.id);
          const allChildren: string[] = [...directChildren];
          for (const childId of directChildren) {
            allChildren.push(...getAllSubfolderIds(childId));
          }
          return allChildren;
        };
        
        const allSubfolderIds = getAllSubfolderIds(id);
        const allSubfolderFiles = get().files.filter(f => allSubfolderIds.includes(f.folderId || ''));
        
        await activeFileService.deleteFolder(id);
        
        const deletedFileIds = [...folderFiles, ...allSubfolderFiles].map(f => f.id);
        
        set((state) => {
          const newFolders = state.folders.filter((f) => f.id !== id && !allSubfolderIds.includes(f.id));
          const newFiles = state.files.filter((f) => !deletedFileIds.includes(f.id));
          
          schedulePersistFoldersCache(newFolders, userId);
          schedulePersistFilesCache(newFiles, userId);
          
          return {
            folders: newFolders,
            files: newFiles,
            selectedFolders: state.selectedFolders.filter((fId) => fId !== id && !allSubfolderIds.includes(fId)),
            selectedFiles: state.selectedFiles.filter((fId) => !deletedFileIds.includes(fId)),
          };
        });
      } catch (error) {
        set({ error: 'Failed to delete folder' });
        throw error;
      }
    },

    updateFolder: async (id: string, updates: Partial<Folder>) => {
      await throwIfManuallySuspended();
      const userId = useAuthStore.getState().user?.id;
      
      try {
        const updatedFolder = await activeFileService.updateFolder(id, updates);
        set((state) => {
          const newFolders = state.folders.map((f) => (f.id === id ? updatedFolder : f));
          schedulePersistFoldersCache(newFolders, userId);
          return { folders: newFolders };
        });
      } catch (error) {
        set({ error: 'Failed to update folder' });
        throw error;
      }
    },

    moveFolder: async (folderId: string, parentId: string | null) => {
      await throwIfManuallySuspended();
      const userId = useAuthStore.getState().user?.id;
      
      try {
        const updateData: any = {};
        if (parentId === null) {
          updateData.parentId = null;
        } else if (parentId !== undefined && parentId !== '') {
          updateData.parentId = parentId;
        }
        
        await activeFileService.updateFolder(folderId, updateData);
        
        set((state) => {
          const newFolders = state.folders.map((f) => 
            f.id === folderId 
              ? { ...f, parentId: parentId === null ? undefined : parentId } 
              : f
          );
          schedulePersistFoldersCache(newFolders, userId);
          return { folders: newFolders };
        });
      } catch (error) {
        console.error('❌ Failed to move folder:', error);
        set({ error: 'Failed to move folder' });
        throw error;
      }
    },

    uploadFile: async (file: File, metadata: Partial<AudioFile>) => {
      await throwIfManuallySuspended();

      const fileId = metadata.id || crypto.randomUUID();
      const typeConversion = (type?: FileType): 'beat' | 'sample' | 'loop' | undefined => {
        if (type === FileType.BEAT) return 'beat';
        if (type === FileType.SAMPLE) return 'sample';
        if (type === FileType.LOOP) return 'loop';
        if (type === FileType.ONESHOT) return 'sample';
        if (type === FileType.PACK) return 'beat';
        return undefined;
      };

      const uploadMetadata = {
        id: fileId,
        type: typeConversion(metadata.type),
        folderId: metadata.folderId,
        tags: metadata.tags,
        bpm: metadata.bpm,
        key: metadata.key,
        name: metadata.name,
        originalName: metadata.originalName,
        status: metadata.status,
        versionType: metadata.versionType,
        beatstarsSellUrl: metadata.beatstarsSellUrl,
        beatstarsPrice: metadata.beatstarsPrice,
        collaborator: metadata.collaborator,
      } as Partial<AudioFile> & {
        id?: string;
        type?: 'beat' | 'sample' | 'loop';
        folderId?: string;
        tags?: any[];
        bpm?: number;
        key?: string;
        status?: FileStatus;
        versionType?: string;
      };

      const initialProgress: UploadProgress = {
        fileId,
        fileName: file.name,
        progress: 0,
        status: 'pending',
      };
      const progressMap = new Map(get().uploadProgress);
      progressMap.set(fileId, initialProgress);
      set({ uploadProgress: progressMap });
      
      uploadWizardProgressService.saveProgress(initialProgress);

      try {
        const uploadedFile = await uploadQueue.add({
          file,
          metadata: uploadMetadata,
          maxRetries: 3,
          onProgress: (progress) => {
            const newProgressMap = new Map(get().uploadProgress);
            newProgressMap.set(fileId, progress);
            set({ uploadProgress: newProgressMap });
            uploadWizardProgressService.saveProgress(progress);
          },
        });

        const userId = useAuthStore.getState().user?.id;
        set((state) => {
          const fileExists = state.files.some(f => f.id === uploadedFile.id);
          if (fileExists) {
            console.warn('⚠️ File already exists in store, skipping duplicate:', uploadedFile.id);
            return state;
          }
          
          if (userId) {
            storageLimitService.clearCache(userId);
          }
          
          const newFiles = [...state.files, uploadedFile];
          schedulePersistFilesCache(newFiles, userId);
          return { files: newFiles };
        });

        setTimeout(() => {
          if ((window as any).__uploadWizardOpen) return;
          const finalMap = new Map(get().uploadProgress);
          finalMap.delete(fileId);
          set({ uploadProgress: finalMap });
          uploadWizardProgressService.removeProgress(fileId);
        }, 2000);
      } catch (error) {
        const err = error instanceof Error ? error : new Error('Upload failed');
        captureException(err, { fileName: file.name, fileId });
        
        const errorMap = new Map(get().uploadProgress);
        const currentProgress = errorMap.get(fileId);
        if (currentProgress) {
          const errorProgress: UploadProgress = {
            ...currentProgress,
            status: 'error',
            error: err.message,
          };
          errorMap.set(fileId, errorProgress);
          set({ uploadProgress: errorMap });
          uploadWizardProgressService.saveProgress(errorProgress);
        }
        throw err;
      }
    },

    deleteFile: async (id: string) => {
      const userId = useAuthStore.getState().user?.id;
      try {
        await activeFileService.deleteFile(id, true);
        set((state) => {
          const newFiles = state.files.filter((f) => f.id !== id);
          schedulePersistFilesCache(newFiles, userId);
          if (userId) {
            storageLimitService.clearCache(userId);
          }
          return {
            files: newFiles,
            selectedFiles: state.selectedFiles.filter((fId) => fId !== id),
          };
        });
      } catch (error) {
        console.error('❌ Failed to delete file:', error);
        set({ error: 'Failed to delete file' });
        throw error;
      }
    },

    updateFile: async (id: string, updates: Partial<AudioFile>) => {
      await throwIfManuallySuspended();
      const userId = useAuthStore.getState().user?.id;
      
      try {
        const updatedFile = await activeFileService.updateFile(id, updates);
        set((state) => {
          const newFiles = state.files.map((f) => {
            if (f.id === id) {
              return { ...f, ...updatedFile };
            }
            return f;
          });
          schedulePersistFilesCache(newFiles, userId);
          return { files: newFiles };
        });
      } catch (error) {
        console.error('❌ Store: Failed to update file', error);
        set({ error: 'Failed to update file' });
        throw error;
      }
    },

    toggleFileStar: async (id: string) => {
      const file = get().files.find((f) => f.id === id);
      if (!file) {
        throw new Error('File not found');
      }

      if (useAuthStore.getState().isSuspended) {
        throw new Error('ACCOUNT_SUSPENDED');
      }

      const previousStarred = file.isStarred;
      const nextStarred = !previousStarred;
      const userId = useAuthStore.getState().user?.id;

      set((state) => {
        const newFiles = state.files.map((f) => (f.id === id ? { ...f, isStarred: nextStarred } : f));
        schedulePersistFilesCache(newFiles, userId);
        return { files: newFiles };
      });

      try {
        await activeFileService.setFileStarred(id, nextStarred);
      } catch (error) {
        set((state) => {
          const rolledBackFiles = state.files.map((f) => (f.id === id ? { ...f, isStarred: previousStarred } : f));
          schedulePersistFilesCache(rolledBackFiles, userId);
          return { files: rolledBackFiles };
        });
        console.error('❌ Store: Failed to toggle star', error);
        set({ error: 'Failed to update file' });
        throw error;
      }
    },

    moveFile: async (fileId: string, folderId: string | null) => {
      await throwIfManuallySuspended();
      const userId = useAuthStore.getState().user?.id;
      
      try {
        await activeFileService.moveFile(fileId, folderId);
        set((state) => {
          const newFiles = state.files.map((f) => (f.id === fileId ? { ...f, folderId: folderId ?? undefined } : f));
          schedulePersistFilesCache(newFiles, userId);
          return { files: newFiles };
        });
      } catch (error) {
        set({ error: 'Failed to move file' });
        throw error;
      }
    },

    selectFile: (id: string) => {
      set((state) => ({
        selectedFiles: [...state.selectedFiles, id],
      }));
    },

    deselectFile: (id: string) => {
      set((state) => ({
        selectedFiles: state.selectedFiles.filter((fId) => fId !== id),
      }));
    },

    clearSelection: () => {
      set({ selectedFiles: [], selectedFolders: [] });
    },

    toggleFileSelection: (id: string) => {
      set((state) => ({
        selectedFiles: state.selectedFiles.includes(id)
          ? state.selectedFiles.filter((fId) => fId !== id)
          : [...state.selectedFiles, id],
      }));
    },

    selectMultiple: (ids: string[]) => {
      set({ selectedFiles: ids });
    },

    selectFolder: (id: string) => {
      set((state) => ({
        selectedFolders: [...state.selectedFolders, id],
      }));
    },

    deselectFolder: (id: string) => {
      set((state) => ({
        selectedFolders: state.selectedFolders.filter((fId) => fId !== id),
      }));
    },

    clearFolderSelection: () => {
      set({ selectedFolders: [] });
    },

    toggleFolderSelection: (id: string) => {
      set((state) => ({
        selectedFolders: state.selectedFolders.includes(id)
          ? state.selectedFolders.filter((fId) => fId !== id)
          : [...state.selectedFolders, id],
      }));
    },

    selectMultipleFolders: (ids: string[]) => {
      set({ selectedFolders: ids });
    },

    clearAllSelections: () => {
      set({ selectedFiles: [], selectedFolders: [] });
    },

    getFilesByType: (type: FileType) => {
      return get().files.filter((file) => file.type === type);
    },

    searchFiles: async (query: string) => {
      set({ isLoading: true, searchQuery: query });
      try {
        const files = await activeFileService.searchFiles(query);
        set({ files, isLoading: false, searchQuery: query });
      } catch (error) {
        set({ error: 'Search failed', isLoading: false, searchQuery: '' });
      }
    },

    setError: (error: string | null) => {
      set({ error });
    },

    getBlobUrl: async (id: string) => {
      try {
        const file = get().files.find((f) => f.id === id);
        if (!file) return null;
        
        if (file.s3_key) {
          return activeFileService.getSignedUrl(file.s3_key, file.userId);
        }
        
        if (file.cdn_url && file.cdn_url !== '') {
          return file.cdn_url;
        }
        
        console.error('❌ No valid URL found for file:', file.name);
        return null;
      } catch (error) {
        console.error('❌ Failed to get blob URL:', error);
        return null;
      }
    },

    preloadUrlsForFiles: (files: AudioFile[]) => {
      if (!files.length) return;
      activeFileService.preloadSignedUrls(files, files.length).catch(() => {});
    },

    getUploadQueueStatus: () => {
      return uploadQueue.getStatus();
    },

    addFileLocally: (file) => {
      const userId = useAuthStore.getState().user?.id;
      set((state) => {
        const newFiles = [...state.files, file];
        schedulePersistFilesCache(newFiles, userId);
        return { files: newFiles };
      });
    },

    patchFileLocally: (id, updates) => {
      const userId = useAuthStore.getState().user?.id;
      set((state) => {
        const newFiles = state.files.map((f) => (f.id === id ? { ...f, ...updates } : f));
        schedulePersistFilesCache(newFiles, userId);
        return { files: newFiles };
      });
    },
  };
});

export function resetFileStoreAfterSignOut(): void {
  useFileStore.setState({
    files: [],
    folders: [],
    selectedFiles: [],
    selectedFolders: [],
    uploadProgress: new Map(),
    isLoading: false,
    error: null,
    searchQuery: '',
    lastFetchedAt: null,
    filesCacheUserId: null,
  });
}
